import type { AspirationFormData } from '../../types/aspirations';
import type { GoalFormData } from '../../types/goals';
import type { ListFormData } from '../../types/lists';

// Each goal's lists are keyed by goal index (0-11) for linking after creation
export interface OnboardingGoalData {
  goal: Omit<GoalFormData, 'aspirationId'>;
  lists: Omit<ListFormData, 'goalId'>[];
}

export const onboardingAspiration: AspirationFormData = {
  title: 'Get Into GodMode',
  description:
    'Your guide to mastering every feature in GodMode. Complete these 12 goals to set up your aspirations, goals, lists, AI assistant, schedules, templates, projections, account, sync, and more — then you\'ll be ready to take control of your life.',
  category: 'Personal',
  priority: 1,
};

export const onboardingGoals: OnboardingGoalData[] = [
  // Goal 1: Create Your First Aspiration (enhanced)
  {
    goal: {
      title: 'Create Your First Aspiration',
      description: 'Learn how to create aspirations — the foundation of the GodMode hierarchy.',
      priority: 1,
      smartCriteria: {
        specific: 'Create a meaningful life aspiration in GodMode',
        measurable: 'One aspiration created with title, category, and description',
        achievable: 'Follow the step-by-step checklist',
        relevant: 'Aspirations are the foundation of the GodMode hierarchy',
        timeBound: 'Within your first session',
      },
    },
    lists: [
      {
        title: 'Set Up an Aspiration',
        description: 'Step-by-step guide to creating your first aspiration.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Think about a life area you want to improve (Career, Health, Financial, Education, Personal, Family, Travel, Social, or Spiritual)' },
          { content: 'Navigate to the Aspirations page from the sidebar' },
          { content: 'Click "Add Aspiration" to open the creation form' },
          { content: 'Enter a clear, inspiring title for your aspiration' },
          { content: 'Select the category that best fits your aspiration' },
          { content: 'Write a description that captures your long-term vision' },
          { content: 'Set a priority to rank this aspiration among your others' },
          { content: 'Save your aspiration and see it appear on the page' },
          { content: 'Edit your aspiration to update the description or category' },
          { content: 'Try filtering aspirations by category using the filter bar' },
          { content: 'Reorder your aspirations by changing priorities' },
        ],
      },
    ],
  },

  // Goal 2: Set Up Your First SMART Goal (enhanced)
  {
    goal: {
      title: 'Set Up Your First SMART Goal',
      description: 'Learn how to create goals using the SMART framework.',
      priority: 2,
      smartCriteria: {
        specific: 'Create a goal linked to an aspiration using the SMART framework',
        measurable: 'One goal created with all 5 SMART criteria filled in',
        achievable: 'The form guides you through each SMART field',
        relevant: 'Goals break aspirations into achievable objectives',
        timeBound: 'Within your first session',
      },
    },
    lists: [
      {
        title: 'Create a SMART Goal',
        description: 'Step-by-step guide to creating your first SMART goal.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Open one of your aspirations and click "Add Goal"' },
          { content: 'Give your goal a clear, action-oriented title' },
          { content: 'Fill in the Specific field: What exactly will you accomplish?' },
          { content: 'Fill in the Measurable field: How will you track progress?' },
          { content: 'Fill in the Achievable field: What steps will you take?' },
          { content: 'Fill in the Relevant field: Why does this matter to your aspiration?' },
          { content: 'Fill in the Time-bound field: What is your deadline?' },
          { content: 'Optionally add success metrics, resources, and potential obstacles' },
          { content: 'Save your goal and see it linked under your aspiration' },
          { content: 'Edit your goal to update the deadline or SMART criteria' },
          { content: 'Filter goals by status using the filter bar' },
        ],
      },
    ],
  },

  // Goal 3: Organize With Lists & Items (enhanced)
  {
    goal: {
      title: 'Organize With Lists & Items',
      description: 'Learn how to create lists and items — the actionable layer where work gets done.',
      priority: 3,
      smartCriteria: {
        specific: 'Create a list with actionable items inside a goal',
        measurable: 'One list with at least 3 items created',
        achievable: 'Follow the checklist to build your first list',
        relevant: 'Lists and items are the actionable layer where work gets done',
        timeBound: 'Within your first session',
      },
    },
    lists: [
      {
        title: 'Build Your First List',
        description: 'Step-by-step guide to creating lists and items.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Open one of your goals and click "Add List"' },
          { content: 'Give the list a descriptive title' },
          { content: 'Add a description explaining what this list covers' },
          { content: 'Set a deadline for completing the list' },
          { content: 'Add at least 3 actionable items to the list' },
          { content: 'Set priorities on your items to order them' },
          { content: 'Use Quick Item Entry to rapidly add multiple items' },
          { content: 'Set a due date, category, and difficulty on one of your items' },
          { content: 'Add notes or a description to an item for more detail' },
          { content: 'Try completing an item and watch the progress update on the goal' },
          { content: 'Undo a completed item using the undo button' },
          { content: 'Transition your list status from "Ready" to "In Progress"' },
          { content: 'Select multiple items and use bulk complete' },
          { content: 'Delete an item you no longer need' },
          { content: 'Edit a list to change its title or description' },
          { content: 'Notice how goal progress flows up to the aspiration progress' },
        ],
      },
    ],
  },

  // Goal 4: Set Up the AI Assistant (unchanged)
  {
    goal: {
      title: 'Set Up the AI Assistant',
      description: 'Configure the AI assistant with your own Anthropic API key.',
      priority: 4,
      smartCriteria: {
        specific: 'Configure the AI assistant with your own Anthropic API key',
        measurable: 'AI is enabled in Settings with a validated API key',
        achievable: 'Anthropic offers free credits for new accounts',
        relevant: 'The AI assistant helps you create better aspirations, goals, lists, and schedules',
        timeBound: 'Within your first week',
      },
    },
    lists: [
      {
        title: 'Create Your Anthropic Account & API Key',
        description: 'Set up an Anthropic account and generate an API key for the AI assistant.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Go to console.anthropic.com and click "Sign Up"' },
          { content: 'Create your account using email or Google sign-in' },
          { content: 'Once logged in, navigate to "API Keys" in the Anthropic dashboard' },
          { content: 'Click "Create Key" and give it a name (e.g. "GodMode")' },
          { content: 'Copy the API key — it starts with "sk-ant-" (save it somewhere safe, you won\'t see it again)' },
          { content: 'Note: New Anthropic accounts include free credits to get started' },
        ],
      },
      {
        title: 'Configure AI in GodMode',
        description: 'Paste your API key and enable AI features in GodMode settings.',
        status: 'ready',
        priority: 2,
        items: [
          { content: 'Open GodMode Settings from the sidebar' },
          { content: 'Find the "AI Configuration" section' },
          { content: 'Paste your Anthropic API key into the API key field' },
          { content: 'Wait for the green checkmark confirming your key is valid' },
          { content: 'Select your preferred Claude model from the dropdown' },
          { content: 'Enable the AI features toggle' },
          { content: 'Note: Your API key is stored locally in your browser and never sent to our servers' },
        ],
      },
      {
        title: 'Try the AI Assistant',
        description: 'Explore the AI features across the app.',
        status: 'ready',
        priority: 3,
        items: [
          { content: 'Open the form to create a new aspiration' },
          { content: 'Look for the AI assistant section and type a request (e.g. "Help me create a health aspiration")' },
          { content: 'Review the AI-generated suggestion and apply it to the form' },
          { content: 'Try using AI when creating a goal — it helps fill in SMART criteria' },
          { content: 'Try using AI to generate items for one of your lists' },
          { content: 'Explore the AI schedule optimizer in the Calendar view' },
          { content: 'Check out AI-generated progress reports on the Reports page' },
        ],
      },
    ],
  },

  // Goal 5: Set Up Schedules & Notifications (enhanced)
  {
    goal: {
      title: 'Set Up Schedules & Notifications',
      description: 'Schedule a list and enable notifications to stay on track.',
      priority: 5,
      smartCriteria: {
        specific: 'Schedule a list and enable notifications',
        measurable: 'At least one list scheduled with notifications turned on',
        achievable: 'Follow the scheduling steps in the calendar',
        relevant: 'Schedules and notifications keep you on track daily',
        timeBound: 'Within your first week',
      },
    },
    lists: [
      {
        title: 'Schedule Your First List',
        description: 'Learn how to schedule lists on the calendar.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Navigate to the Calendar page from the sidebar' },
          { content: 'Click on a time slot or use "Add Schedule" to create a new schedule' },
          { content: 'Select a list you want to work on at that time' },
          { content: 'Set the start time and duration for the scheduled session' },
          { content: 'Review the schedule on your calendar view' },
          { content: 'Try the AI schedule optimizer to get suggestions for optimal times' },
          { content: 'Navigate to the Schedules page to view all your schedules' },
        ],
      },
      {
        title: 'Enable Notifications',
        description: 'Turn on notifications to receive reminders for scheduled tasks.',
        status: 'ready',
        priority: 2,
        items: [
          { content: 'Open Settings from the sidebar' },
          { content: 'Find the notifications preferences section' },
          { content: 'Enable notifications to receive reminders' },
          { content: 'Allow browser notifications when prompted' },
          { content: 'You\'ll now receive reminders for upcoming scheduled tasks' },
        ],
      },
    ],
  },

  // Goal 6: Use List Templates (NEW)
  {
    goal: {
      title: 'Use List Templates',
      description: 'Discover and use list templates to quickly create pre-built lists.',
      priority: 6,
      smartCriteria: {
        specific: 'Browse, use, and create list templates',
        measurable: 'One list created from a template, one custom template created',
        achievable: 'The templates gallery is ready to explore',
        relevant: 'Templates save time by providing pre-built lists for common tasks',
        timeBound: 'Within your first week',
      },
    },
    lists: [
      {
        title: 'Explore Templates',
        description: 'Learn how to browse and use list templates.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Browse the List Templates gallery from the sidebar' },
          { content: 'View a template to see its items and description' },
          { content: 'Create a new list from a template using "Use Template"' },
          { content: 'Create your own custom template from an existing list' },
          { content: 'Filter templates by category' },
        ],
      },
    ],
  },

  // Goal 7: Track Your Progress (NEW)
  {
    goal: {
      title: 'Track Your Progress',
      description: 'Use projections and reports to monitor your progress and predict completion.',
      priority: 7,
      smartCriteria: {
        specific: 'View projections, velocity metrics, and AI progress reports',
        measurable: 'Burndown chart viewed, velocity checked, one AI report generated',
        achievable: 'Complete some items first to generate meaningful data',
        relevant: 'Tracking progress helps you stay motivated and adjust your approach',
        timeBound: 'Within your first two weeks',
      },
    },
    lists: [
      {
        title: 'View Projections & Reports',
        description: 'Explore progress tracking tools including burndown charts and AI reports.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Navigate to the Projections page from the sidebar' },
          { content: 'View the burndown chart showing your completion progress' },
          { content: 'Check velocity metrics to see your daily/weekly completion rate' },
          { content: 'View the completion timeline for predicted finish dates' },
          { content: 'Navigate to the Reports page from the sidebar' },
          { content: 'Generate an AI progress report for one of your aspirations' },
          { content: 'Review the strengths, weaknesses, and recommendations in the report' },
        ],
      },
    ],
  },

  // Goal 8: Manage Your Account (NEW)
  {
    goal: {
      title: 'Manage Your Account',
      description: 'Set up your profile and secure your account with two-factor authentication.',
      priority: 8,
      smartCriteria: {
        specific: 'Update your profile and enable two-factor authentication',
        measurable: 'Profile updated with display name and avatar, 2FA enabled',
        achievable: 'Settings and profile pages guide you through each step',
        relevant: 'A complete profile and secured account protect your data',
        timeBound: 'Within your first week',
      },
    },
    lists: [
      {
        title: 'Set Up Your Profile',
        description: 'Update your profile information and change your password.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Navigate to Profile from the menu' },
          { content: 'Update your display name' },
          { content: 'Upload or change your avatar' },
          { content: 'Navigate to Settings from the menu' },
          { content: 'Change your password using the Change Password option' },
        ],
      },
      {
        title: 'Secure Your Account',
        description: 'Enable two-factor authentication for extra security.',
        status: 'ready',
        priority: 2,
        items: [
          { content: 'Open the Two-Factor Authentication section in Settings' },
          { content: 'Enable 2FA by scanning the QR code with your authenticator app' },
          { content: 'Verify 2FA is working by entering a code' },
        ],
      },
    ],
  },

  // Goal 9: Back Up & Sync Your Data (NEW)
  {
    goal: {
      title: 'Back Up & Sync Your Data',
      description: 'Set up cloud sync and create backups to keep your data safe.',
      priority: 9,
      smartCriteria: {
        specific: 'Enable cloud sync and create a data backup',
        measurable: 'Cloud sync connected, one manual backup created',
        achievable: 'Settings guide you through connecting Google Drive and creating backups',
        relevant: 'Backups and sync protect your data across devices',
        timeBound: 'Within your first week',
      },
    },
    lists: [
      {
        title: 'Set Up Cloud Sync',
        description: 'Connect your Google account and enable cloud sync.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Open Settings and find the Cloud Sync section' },
          { content: 'Connect your Google account for cloud sync' },
          { content: 'Enable automatic cloud sync' },
          { content: 'Trigger a manual sync using "Sync Now" from the menu' },
        ],
      },
      {
        title: 'Create a Backup',
        description: 'Create and verify a manual backup of your data.',
        status: 'ready',
        priority: 2,
        items: [
          { content: 'Open Settings and find the Backup section' },
          { content: 'Create a manual backup of your data' },
          { content: 'Verify the backup was created successfully' },
          { content: '(Optional) Try restoring from a backup to test the restore flow' },
        ],
      },
    ],
  },

  // Goal 10: Explore Help & Feedback (NEW)
  {
    goal: {
      title: 'Explore Help & Feedback',
      description: 'Visit the help center and submit feedback to help improve GodMode.',
      priority: 10,
      smartCriteria: {
        specific: 'Visit the help center and submit feedback',
        measurable: 'One help article read, one feedback message submitted',
        achievable: 'Help center and feedback are accessible from the menu',
        relevant: 'Help resources answer questions and feedback improves the app',
        timeBound: 'Within your first two weeks',
      },
    },
    lists: [
      {
        title: 'Get Help & Give Feedback',
        description: 'Explore the help center and share your thoughts on GodMode.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Navigate to the Help Center from the menu' },
          { content: 'Read a help article to learn more about a feature' },
          { content: 'Open the Send Feedback option from the menu' },
          { content: 'Submit a feedback message with your thoughts on GodMode' },
        ],
      },
    ],
  },

  // Goal 11: Manage Your Subscription (NEW)
  {
    goal: {
      title: 'Manage Your Subscription',
      description: 'Review your subscription plan and explore available tiers.',
      priority: 11,
      smartCriteria: {
        specific: 'Review your subscription status and explore plan options',
        measurable: 'Subscription page visited, available tiers reviewed',
        achievable: 'Subscription information is available in Settings',
        relevant: 'Understanding your plan helps you access the right features',
        timeBound: 'Within your first two weeks',
      },
    },
    lists: [
      {
        title: 'Review Your Plan',
        description: 'Check your subscription status and explore available plans.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Navigate to Settings and find the Subscription section' },
          { content: 'Review your current subscription plan and features' },
          { content: 'Explore the available subscription tiers (Free, Pro, Enterprise)' },
          { content: '(Optional) Upgrade your plan or manage billing through the Stripe portal' },
        ],
      },
    ],
  },

  // Goal 12: Achieve GodMode (enhanced)
  {
    goal: {
      title: 'Achieve GodMode',
      description: 'You\'ve learned every feature — now master the system.',
      priority: 12,
      smartCriteria: {
        specific: 'Have a fully configured GodMode setup with multiple aspirations, goals, and active schedules',
        measurable: 'At least 3 aspirations with linked goals and lists, AI configured, schedules active',
        achievable: 'You\'ve learned every feature by completing the previous goals',
        relevant: 'This is the culmination — full control over your life planning',
        timeBound: 'Within your first two weeks',
      },
    },
    lists: [
      {
        title: 'Master the System',
        description: 'Final checklist to confirm you\'re in GodMode.',
        status: 'ready',
        priority: 1,
        items: [
          { content: 'Have at least 3 aspirations covering different life areas' },
          { content: 'Have at least one SMART goal linked to each aspiration' },
          { content: 'Have lists with items under each goal' },
          { content: 'Have the AI assistant configured and working' },
          { content: 'Have at least one active schedule with notifications' },
          { content: 'Complete a full list and watch progress cascade up through your goal and aspiration' },
          { content: 'Check your progress reports to see an overview of your journey' },
          { content: 'Verify you have used a list template to create a list' },
          { content: 'Confirm you have viewed your projections and velocity metrics' },
          { content: 'Confirm your account has 2FA enabled' },
          { content: 'Confirm cloud sync is active and your data is backed up' },
          { content: 'Delete the "Get Into GodMode" aspiration to confirm delete with cascade works' },
          { content: 'You\'re now in GodMode — you\'ve mastered every feature!' },
        ],
      },
    ],
  },
];
